/* =============================================================================
   Soulog Sense — bundle cards + subtotal.

   Loaded only by the commercial (non pre-launch) state of the Soulog Sense
   product layout; see snippets/product-information.liquid.

   Two jobs, both read-only as far as the theme is concerned:

   1. Bundle cards drive the theme's own control for their option. Clicking a
      card writes the value into that <select> / radio and dispatches a bubbling
      `change`, which is what <variant-selects> listens for — so pricing, the
      gallery, the URL and the sold-out state all keep going through the theme's
      stock code path. Nothing here touches the cart or the product form.

   2. Prices are resolved from the variant table (snippets/
      product-soulog-variant-data.liquid) using the option values currently
      selected in the DOM, NOT from the theme's `currentVariant`. The theme
      dispatches `product:variant-change` before its async section re-render has
      updated `#SelectedVariant-*`, so that object can still describe the
      previous selection; reading the controls is synchronous and cannot lag.

   The theme's re-render only replaces the price / inventory / sku containers and
   syncs `data-available` onto existing inputs (assets/product.js
   `renderVariantInfo`), so every node this file holds on to survives it.
   ========================================================================== */

(function () {
	const panel = document.querySelector(".pi-soulog");
	if (!panel) return;

	const dataNode = panel.querySelector("[data-pi-variants]");
	if (!dataNode) return;

	let variants = [];
	try {
		variants = JSON.parse(dataNode.textContent) || [];
	} catch (error) {
		return;
	}
	if (!variants.length) return;

	const selects = panel.querySelector("variant-selects");
	const bundle = panel.querySelector("[data-pi-bundle]");
	const subtotal = panel.querySelector("[data-pi-subtotal]");
	if (!selects) return;
	if (!bundle && !subtotal) return;

	const moneyFormat =
		(window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || "${{amount}}";

	// formatMoney is a theme global (assets/vendor.min.js), used the same way by
	// free-shipping.js and shipping-estimator.js.
	function money(cents) {
		if (typeof window.formatMoney === "function") {
			return window.formatMoney(cents, moneyFormat);
		}
		return (cents / 100).toFixed(2);
	}

	// Re-queried on every read: the theme replaces innerHTML of some containers,
	// and the option fieldsets are rendered in `product.options_with_values`
	// order, which is the order the variant table's `options` array uses.
	function fieldsets() {
		return Array.from(selects.querySelectorAll("fieldset"));
	}

	function currentOptions() {
		return fieldsets().map(function (fieldset) {
			const dropdown = fieldset.querySelector("select");
			if (dropdown) return dropdown.value;
			const checked = fieldset.querySelector("input:checked");
			return checked ? checked.value : null;
		});
	}

	function findVariant(options) {
		for (let i = 0; i < variants.length; i++) {
			const candidate = variants[i];
			let match = true;
			for (let j = 0; j < candidate.options.length; j++) {
				if (candidate.options[j] !== options[j]) {
					match = false;
					break;
				}
			}
			if (match) return candidate;
		}
		return null;
	}

	function quantity() {
		const input = panel.querySelector(".quantity__input");
		const value = input ? parseInt(input.value, 10) : 1;
		return Number.isFinite(value) && value > 0 ? value : 1;
	}

	function paintBundle(options) {
		if (!bundle) return;
		const index = parseInt(bundle.dataset.optionIndex, 10);
		if (!Number.isFinite(index)) return;

		bundle.querySelectorAll("[data-bundle-card]").forEach(function (card) {
			const value = card.dataset.bundleValue;
			// What the shopper would pay for this card with every OTHER option left
			// exactly where they put it.
			const probe = options.slice();
			probe[index] = value;

			const variant = findVariant(probe);
			const usable = !!variant && variant.available;
			const selected = options[index] === value;

			card.classList.toggle("is-selected", selected);
			card.classList.toggle("is-disabled", !usable);

			const radio = card.querySelector("[data-bundle-radio]");
			if (radio) {
				radio.checked = selected;
				radio.disabled = !usable;
			}

			if (!variant) return;

			const discounted = variant.compare > variant.price;
			const now = card.querySelector("[data-bundle-now]");
			const was = card.querySelector("[data-bundle-was]");
			const save = card.querySelector("[data-bundle-save]");
			const saveAmount = card.querySelector("[data-bundle-save-amount]");

			if (now) now.textContent = money(variant.price);
			if (was) {
				was.textContent = money(variant.compare);
				was.hidden = !discounted;
			}
			if (save) save.hidden = !discounted;
			if (saveAmount) saveAmount.textContent = money(variant.compare - variant.price);
		});
	}

	function paintSubtotal(options) {
		if (!subtotal) return;
		const variant = findVariant(options);
		const now = subtotal.querySelector("[data-subtotal-now]");
		const was = subtotal.querySelector("[data-subtotal-was]");

		if (!variant) {
			if (was) was.hidden = true;
			return;
		}

		const count = quantity();
		const discounted = variant.compare > variant.price;
		if (now) now.textContent = money(variant.price * count);
		if (was) {
			was.textContent = money(variant.compare * count);
			was.hidden = !discounted;
		}
	}

	function paint() {
		const options = currentOptions();
		paintBundle(options);
		paintSubtotal(options);
	}

	function selectOption(index, value) {
		const fieldset = fieldsets()[index];
		if (!fieldset) return;

		const dropdown = fieldset.querySelector("select");
		if (dropdown) {
			dropdown.value = value;
			dropdown.dispatchEvent(new Event("change", { bubbles: true }));
			return;
		}

		const input = fieldset.querySelector('input[value="' + CSS.escape(value) + '"]');
		if (!input) return;
		input.checked = true;
		input.dispatchEvent(new Event("change", { bubbles: true }));
	}

	if (bundle) {
		bundle.addEventListener("change", function (event) {
			const radio = event.target.closest("[data-bundle-radio]");
			if (!radio) return;
			selectOption(parseInt(bundle.dataset.optionIndex, 10), radio.value);
		});

		// The whole card is a target, not just the title row. Activating the label
		// has already checked the radio and fired `change` by the time this runs,
		// so the guard keeps one click from triggering two section re-fetches.
		bundle.addEventListener("click", function (event) {
			const card = event.target.closest("[data-bundle-card]");
			if (!card) return;
			const radio = card.querySelector("[data-bundle-radio]");
			if (!radio || radio.disabled || radio.checked) return;
			radio.checked = true;
			selectOption(parseInt(bundle.dataset.optionIndex, 10), radio.value);
		});
	}

	// Fires for the theme's own controls and for the ones we drive above, since
	// the event we dispatch bubbles.
	selects.addEventListener("change", paint);

	const quantityInput = panel.querySelector(".quantity__input");
	if (quantityInput) {
		quantityInput.addEventListener("change", paint);
		quantityInput.addEventListener("input", paint);
	}

	paint();
})();
